import { useEffect } from "react"
import { Link, useSearchParams } from "react-router"
import { useQueryClient } from "@tanstack/react-query"
import cx from "classnames"

import WizardNavigation from "../components/WizardNavigation"
import GovtWizardContent from "../sections/GovernmentWizard/GovtWizardContent"

import { useGovernmentStore } from "../stores/useGovernmentStore"
import {
  useAccount,
  useAccountId,
  useGetGovtAppWizard,
  useProfile,
  useProjectList,
  useCreateMembership,
} from "../hooks"
import { useIsCheckoutSuccess } from "../hooks/useIsCheckoutSuccess"
import { useAccessToken } from "../stores"
import { AccountTypes } from "@/types/account"
import { GovtApWizardType } from "@/types/govtAppWizard"
import { Profile } from "@/types"
import { ProjectListTypes } from "@/types/program"

const GovernmentWizard = () => {
  const queryClient = useQueryClient()
  const accountId = useAccountId()
  const accessToken = useAccessToken()
  const [searchParams, setSearchParams] = useSearchParams()

  const step = searchParams.get("step")

  const {
    enabledSteps,
    setEnabledSteps,
    setMultipleActionKeys,
    setBookConsult,
    setManagement,
  } = useGovernmentStore()

  const { data: account, isLoading: accountIsLoading } = useAccount<
    AccountTypes,
    Error
  >(queryClient, accountId)

  const { data: profile, isLoading: profileIsLoading } = useProfile<
    Profile,
    Error
  >(queryClient, {
    enabled: !!accessToken,
  })

  const { data: projectList, isLoading: projectListIsLoading } = useProjectList<
    ProjectListTypes,
    Error
  >(queryClient, accountId, {})

  const { data: govtAppWizard, isLoading: govtAppWizardIsLoading } =
    useGetGovtAppWizard<GovtApWizardType, Error>(queryClient, accountId)

  // Handle membership creation
  const membershipSuccessToken =
    searchParams.get("checkout-success")?.slice(2, -1) || ""

  const { status: membershipStatus } = useCreateMembership(
    accountId,
    membershipSuccessToken,
    "",
    {
      enabled: !!accountId && !!membershipSuccessToken,
    }
  )

  const createMembershipIsLoading =
    membershipSuccessToken && membershipStatus === "pending"

  const isLoadingArr = [
    accountIsLoading,
    profileIsLoading,
    projectListIsLoading,
    govtAppWizardIsLoading,
    createMembershipIsLoading,
  ]

  const isLoading = isLoadingArr.some(Boolean)

  const handleActiveStep = (newActiveStep: number) => {
    searchParams.set("step", String(newActiveStep))

    setSearchParams(searchParams)
  }

  useEffect(() => {
    if (!govtAppWizard) return

    const {
      completed,
      has_been_started,
      selected_action_keys,
      selected_implementation,
    } = govtAppWizard

    let steps = [true, false, false, false, false]

    if (completed) {
      steps = [true, true, true, true, true]
    } else if (has_been_started) {
      const hasSelectedActions = Array.isArray(selected_action_keys)
      const hasImplementation = selected_implementation !== null

      if (hasSelectedActions && !hasImplementation) {
        steps = [true, true, true, true, false]
      } else if (!hasSelectedActions && !hasImplementation) {
        steps = [true, true, true, false, false]
      }
    }

    setEnabledSteps(steps)
  }, [
    govtAppWizard,
    govtAppWizard?.completed,
    govtAppWizard?.has_been_started,
    govtAppWizard?.selected_action_keys,
    govtAppWizard?.selected_implementation,
    setEnabledSteps,
  ])

  useEffect(() => {
    if (step === null && govtAppWizard?.completed === true) {
      searchParams.set("step", "5")
    }

    if (step === null && govtAppWizard?.completed === false) {
      searchParams.set("step", "1")
    }

    setSearchParams(searchParams, { replace: true })
  }, [govtAppWizard?.completed, searchParams, setSearchParams, step])

  useEffect(() => {
    if (
      govtAppWizard?.selected_action_keys &&
      govtAppWizard?.selected_action_keys?.length > 0
    ) {
      setMultipleActionKeys(govtAppWizard.selected_action_keys)
    }
  }, [govtAppWizard?.selected_action_keys, setMultipleActionKeys])

  useEffect(() => {
    if (govtAppWizard?.selected_implementation === "ncx_services") {
      setBookConsult(true)
    } else if (govtAppWizard?.selected_implementation === "self") {
      setManagement("self")
    }
  }, [govtAppWizard?.selected_implementation, setBookConsult, setManagement])

  useIsCheckoutSuccess()

  return (
    <div className="bg-white min-h-full">
      <div className="relative w-full flex lg:justify-center pt-5 lg:pt-10 px-5 lg:px-10">
        <WizardNavigation
          navItems={[
            {
              className: "govt-app-navigation-item-page-1",
              text: "1. Introduction",
            },
            {
              className: "govt-app-navigation-item-introduction-page-2",
              text: "2. Eligibility Quiz",
            },
            {
              className: "govt-app-navigation-item-page-3",
              text: "3. Select Approach",
            },
            {
              className: "govt-app-navigation-item-page-4",
              text: "4. Take action",
            },
            {
              className: "govt-app-navigation-item-page-5",
              text: "5. Plan Summary",
            },
          ]}
          activeStep={Number(step)}
          enabledSteps={enabledSteps}
          handleActiveStep={handleActiveStep}
          testid="government-wizard-navigation"
        />

        <Link
          to="/"
          className={cx(
            "govt-app-close-button",
            "absolute right-5 xl:right-10 top-5 lg:top-10 flex items-center justify-center",
            "text-3xl w-6 h-6 text-dusk-500 leading-6 tracking-[0.02em] cursor-pointer"
          )}
          aria-label="Close Government Wizard"
        >
          &times;
        </Link>
      </div>

      <GovtWizardContent
        isLoading={isLoading}
        account={account}
        projectList={projectList}
        govtAppWizard={govtAppWizard}
        profile={profile}
      />
    </div>
  )
}

export default GovernmentWizard
